/* eslint-disable spire/export-styles */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { css } from "styled-components";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";

const enum fields {
    destination = "destination",
    overrideTitle = "overrideTitle",
    textColor = "textColor",
    linkType = "linkType",
    linkValue = "linkValue",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.destination]: LinkFieldValue;
        [fields.overrideTitle]: string;
        [fields.textColor]: string;
        [fields.linkType]: "tel" | "email" | "link" | "url";
        [fields.linkValue]: string;
    };
}

const parseLink = (linkValue: string, linkType: string) => {
    var parsedLink: string = "";

    switch (linkType) {
        case "tel":
            parsedLink = `tel: ${linkValue}`;
            break;

        case "email":
            parsedLink = `mailto:${linkValue}`;
            break;

        case "url":
            parsedLink = `${linkValue}`;
            break;

        default:
            parsedLink = `${linkValue}`;
            break;
    }

    return parsedLink;
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const link = getLink(state, ownProps.fields.destination);
    return {
        url: link?.url,
        title: link?.title,
    };
};

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const Link: React.FC<Props> = ({ url, title, fields }) => {
    const wrapperStyle = {
        css: css`
            display: block;
            width: 100%;
        `,
    };

    const linkStyle = {
        css: css`
            color: ${fields.textColor};
        `,
    };

    if (url) {
        return (
            <StyledWrapper {...wrapperStyle}>
                <a href={url} style={{ color: fields.textColor }}>
                    {fields.overrideTitle || title || url}
                </a>
            </StyledWrapper>
        );
    }

    let linkUrl = parseLink(fields.linkValue, fields.linkType);

    return (
        <StyledWrapper {...wrapperStyle}>
            <a href={linkUrl} style={{ color: fields.textColor }}>
                {fields.linkValue}
            </a>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Link),
    definition: {
        group: "Basic",
        icon: "Link",
        fieldDefinitions: [
            {
                name: fields.linkType,
                displayName: "Link Type",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Tel Link",
                        value: "tel",
                    },
                    {
                        displayName: "Email Link",
                        value: "email",
                    },
                    {
                        displayName: "Url",
                        value: "url",
                    },
                    {
                        displayName: "Default Link",
                        value: "link",
                    },
                ],
                defaultValue: "link",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.textColor,
                editorTemplate: "ColorPickerField",
                displayName: "Link  Color",
                defaultValue: "#f16906",
                fieldType: "General",
            },
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                isRequired: true,
                isVisible: (item: HasFields) => item.fields.linkType === "link",
            },
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: (item: HasFields) => item.fields.linkType === "link",
            },
            {
                name: fields.linkValue,
                editorTemplate: "TextField",
                displayName: "Link URL",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: (item: HasFields) => item.fields.linkType !== "link",
            },
        ],
    },
};

export default widgetModule;
